// src/shared/tour/WalkthroughTourProvider.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ChangelogWalkthrough } from "@shared/changelog/types";
import { canSeeChangelog } from "@shared/config/featureFlags";
import { useAppSelector } from "@redux/hooks";
import { GuidedTour, TourStep } from "./GuidedTour";

/**
 * The cross-route half of the What's new walkthrough (CTX-510 §2): take an
 * entry's `walkthrough`, travel to the screen the change landed on, and run the
 * tour there.
 *
 * ── Why a context, and why here ─────────────────────────────────────────────
 *
 * The launch outlives the component that starts it. The trigger is the What's
 * new modal in the SideMenu footer, which closes on the way out; the tour has
 * to appear on a screen rendered through `<Outlet />`. Local state in the modal
 * dies with it, and props cannot be threaded between two subtrees that only
 * meet at the app shell — so the state lives at `AppLayout`, the nearest
 * ancestor of both.
 *
 * It is deliberately **not** a redux slice. The store persists only
 * `selection`, so a slice would mean either widening the persist whitelist for
 * state that must *not* survive a reload, or a non-persisted slice whose only
 * consumers are one launcher and one mount. A half-finished tour coming back
 * after a refresh would be a bug, and holding it in a context makes that
 * impossible by construction rather than by remembering to clear it.
 *
 * ── The seam with the CTA ───────────────────────────────────────────────────
 *
 * `useLaunchWalkthrough` is the single entry point. CTX-516 (the "Walk me
 * through it" button) imports it; nothing else spells this behaviour out. Two
 * spellings of it is how the button and the tour drift apart.
 */

/** A launch in flight: where it is going, what to show, and which attempt it is. */
interface PendingWalkthrough {
	/** Destination pathname, query and hash already stripped. */
	pathname: string;
	steps: TourStep[];
	/**
	 * Whether the destination has actually been reached. `navigate` does not
	 * land synchronously, so the tour must not mount against the screen the
	 * user is leaving — and once it *has* landed, navigating elsewhere is the
	 * signal to drop the launch instead of holding it for a return visit.
	 */
	arrived: boolean;
	/**
	 * Distinguishes one launch from the next so a re-launch remounts
	 * `GuidedTour` (and restarts it at step one) instead of reusing the
	 * instance's step index. A monotonic counter rather than a clock — two
	 * launches inside the same millisecond are a plausible double-click.
	 */
	nonce: number;
	/**
	 * What the launcher wants told when the tour ends of its own accord.
	 *
	 * The walkthrough itself carries no entry id — it is the authored contract,
	 * a route and some steps — so the caller supplies the meaning instead of
	 * this file learning about the changelog. Today that caller is the What's
	 * new modal marking the entry read (CTX-500 §2.6).
	 */
	onCompleted?: () => void;
}

interface LaunchOptions {
	/**
	 * Called when the tour is **finished or explicitly skipped** — not when the
	 * reader simply navigates away.
	 *
	 * That distinction is the whole of §2.6's rule for an entry with a
	 * walkthrough: reaching the end or pressing `Skip tour` is the reader
	 * saying they are through it, while wandering off is not, and an entry
	 * marked read by wandering off would never come back.
	 */
	onCompleted?: () => void;
}

type LaunchWalkthrough = (
	walkthrough: ChangelogWalkthrough,
	options?: LaunchOptions
) => void;

// Only the launcher is shared. The pending launch stays private to the
// provider, which is also the only thing that renders it — so there is no
// reader context to re-render anyone.
const LaunchWalkthroughContext = createContext<LaunchWalkthrough | null>(null);

/** Query and hash are addressing, not identity — the tour runs on the screen. */
function toPathname(path: string): string {
	return path.split("?")[0].split("#")[0];
}

/**
 * One key per destination, so completing the tour of one screen does not
 * suppress another's.
 *
 * Exported for the tests, which assert the key rather than re-deriving it.
 */
export function walkthroughStorageKey(pathname: string): string {
	return `ctx-walkthrough-tour:${toPathname(pathname)}`;
}

export function WalkthroughTourProvider({ children }: { children: React.ReactNode }): React.ReactElement {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [pending, setPending] = useState<PendingWalkthrough | null>(null);
	const launchCount = useRef(0);

	const launchWalkthrough = useCallback<LaunchWalkthrough>(
		(walkthrough, options) => {
			const destination = toPathname(walkthrough.path);
			// `steps` is optional in the contract (CTX-519): an entry can name
			// where a change landed without touring it. That launch is a plain
			// navigation — arriving with nothing to spotlight is the honest
			// outcome, not an empty dialog.
			const steps = walkthrough.steps ?? [];
			if (steps.length > 0) {
				setPending({
					pathname: destination,
					steps,
					arrived: false,
					nonce: ++launchCount.current,
					onCompleted: options?.onCompleted,
				});
			} else {
				// Nothing to complete, so the navigation itself is the whole of
				// it — and it counts as having been taken (§2.6): the reader
				// asked to be shown where the change landed and was.
				setPending(null);
				options?.onCompleted?.();
			}
			navigate(walkthrough.path);
		},
		[navigate]
	);

	// Track arrival and departure. Both transitions are driven by the route, so
	// they belong to the same effect: the tour opens when the destination is
	// reached and the launch is dropped when the user leaves without finishing.
	useEffect(() => {
		if (!pending) return;
		const isAtDestination = pathname === pending.pathname;
		if (isAtDestination === pending.arrived) return;
		setPending(isAtDestination ? { ...pending, arrived: true } : null);
	}, [pathname, pending]);

	/**
	 * The tour reported itself finished or skipped — the only path that tells
	 * the launcher so. Leaving the destination clears `pending` in the effect
	 * above without coming through here, which is exactly the difference §2.6
	 * rests on.
	 */
	const finishTour = useCallback(() => {
		// Read from the closure rather than inside a `setState` updater: an
		// updater must stay pure, and React may run it twice.
		pending?.onCompleted?.();
		setPending(null);
	}, [pending]);

	return (
		// The provided value is the `useCallback` itself, which only changes
		// identity with `navigate` — so a component holding the launcher is
		// never re-rendered by a tour starting or ending.
		<LaunchWalkthroughContext.Provider value={launchWalkthrough}>
			{children}
			{pending?.arrived && (
				<GuidedTour
					key={pending.nonce}
					steps={pending.steps}
					storageKey={walkthroughStorageKey(pending.pathname)}
					testId="walkthrough-tour"
					ariaLabel="What's new walkthrough"
					trigger="explicit"
					onDismiss={finishTour}
				/>
			)}
		</LaunchWalkthroughContext.Provider>
	);
}

/**
 * The provider plus the gate that decides whether it exists at all — what
 * `AppLayout` mounts.
 *
 * The gate is `canSeeChangelog`, the same helper behind the `/changelog` route,
 * the footer chip, the What's new card, its modal and the `New` tags. The tour
 * is the sixth of those surfaces and the one that reads least like one, since
 * it runs on a product screen rather than on changelog furniture — but it
 * exists only because an entry announced it, so whatever rule decides who
 * sees the changelog has to reach it too. Today that rule lets every tenant
 * type in (CTX-823); while production was `GLOBAL_ADMIN` only (CTX-521),
 * gating on `isChangelogEnabled()` alone would have let a tenant walk through
 * a change the other five hid from them.
 *
 * Ungated, the children render exactly as they would have: `useLaunchWalkthrough`
 * no-ops without a provider, so nothing downstream has to know.
 */
export function WalkthroughTourBoundary({ children }: { children: React.ReactNode }): React.ReactElement {
	// Read from the store here for the same reason `PrivateRoutes` does: this is
	// the ancestor whose scope covers both the launch and the destination.
	const { TenantType: tenantType } = useAppSelector((state) => state.userProfile);
	if (!canSeeChangelog(tenantType)) return <>{children}</>;
	return <WalkthroughTourProvider>{children}</WalkthroughTourProvider>;
}

/**
 * Navigate to where a change landed and run its tour on arrival.
 *
 * No-ops outside a `WalkthroughTourProvider`, which is what a tenant without
 * the changelog gets: `AppLayout` provides it behind `canSeeChangelog`, the
 * same helper as the other five changelog surfaces. A caller does not need to
 * know that — the CTA's own reachability gate (CTX-516) decides whether the
 * button exists, and this stays inert rather than throwing where the changelog
 * is switched off.
 */
export function useLaunchWalkthrough(): LaunchWalkthrough {
	const launch = useContext(LaunchWalkthroughContext);
	return useMemo(() => launch ?? (() => undefined), [launch]);
}
